import {_} from 'lodash';
import {
    ICondition,
    IExportTemplate,
    IKnockoutComponent,
    IMessageData,
    IObjectData,
    IRouteDefinitionParams,
    ISyncField,
    TSyncfield
} from '../../index';
import {nsRoute} from "../../util/route";
import {TSyncfield_Department, TSyncfield_Group} from "../../constants/Syncfields";
import * as ko from "knockout";
import {ErrorTracker} from "../../error.tracker";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {global, lm} from "../../appContext";
import {ButtonManager} from "../../util/ButtonManager";
import {CREATING_MESSAGE, EMPTY_STRING, PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../constants/Language";
import {nsSyncFields} from "../../req/nsSyncFields";
import {
    createErrorMessage,
    getObjectDefinition,
    getRawSyncFieldValue,
    getSourceTranslation,
    getSyncFieldValue,
    isNullOrUndefined,
    isNullOrUndefinedOrEmptyString,
    isPartOf
} from "../../util/misc";
import {
    getCancelButton,
    getDiscardButton,
    getExitButton,
    getNextButton,
    getPreviousButton,
    getSubmitButton
} from "../../constants/RouteDefinitions";
import {IPickerOptions} from "../picker/AbstractPicker";
import {convertFromServerString, convertToServerString, validateServerDate} from "../../util/datetime";
import {IComboboxOptions_Department} from "../combobox/department";
import {INPUT_DATE_FORMAT, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import {nsWizard} from "./nsWizard";
import PromiseRequest from "../../req/PromiseRequest";
import {getSearchObjectParams, getSearchParamsFn} from "../../util/request";
import {nsRuleset} from "../ruleset/nsRuleset";
import {IComboboxV2Options} from "../combobox/v2";
import {nsTooltip} from "../../util/nsTooltip";
import {IComboboxOptions_Ruleset} from "../combobox/ruleset";
import {moment} from 'moment';
import Syncfield_mandatory = nsWizard.Syncfield_mandatory;
import Syncfield_optional = nsWizard.Syncfield_optional;
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ButtonOrder = nsRoute.ButtonOrder;
import AbstractWizardHtml from './AbstractWizard.html?raw';

export let template = AbstractWizardHtml;

export enum Step {
    ExportTemplate,
    Name,
    Optional,
    Members,
    Summary,
    Processing,
    Success,
    Error,
}

export interface ISyncFieldMutable<T = TSyncfield_Group> {
    id: KnockoutObservable<number | null>
    mandatory: boolean
    name: TSyncfield_Group
    syncfield: ISyncField<T> | undefined
    value: KnockoutObservable<string | null>
}

interface IStepDetails {
    step: Step
    name: string | (() => string)
    fraction?: boolean
    prev?: Step
}

enum ParentType {
    Parent,
    Synctemplate,
}

export abstract class AbstractWizard extends ParsedRouteDataDecorator {
    private exportTemplates: IExportTemplate[] = []
    protected groupLink: string = '';
    private readonly ObjectTypeId = ObjectTypeId;
    private readonly ParentType = ParentType
    private readonly Step = Step;
    private readonly et = new ErrorTracker();
    private readonly getDerivedValue = nsSyncFields.getDerivedValue
    private readonly isNameAndSourceIdSame: KnockoutObservable<boolean> = ko.observable(true)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly pageButtons: ButtonManager = new ButtonManager()
    private readonly parentType: KnockoutObservable<ParentType | null> = ko.observable(null)
    private readonly req_searchexporttemplate: PromiseRequest = global.getPromiseRequest('searchexporttemplate')
    private readonly req_getconditions: PromiseRequest = global.getPromiseRequest('getconditions')
    private resp_getconditions: ICondition[] = []
    private readonly validateServerDate = validateServerDate
    protected syncfields_all: ISyncField<TSyncfield_Group>[] = [];
    protected readonly abstract req_submit: PromiseRequest
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly mutables: KnockoutObservableArray<ISyncFieldMutable> = ko.observableArray();
    protected readonly step: KnockoutObservable<Step | null> = ko.observable(null);
    private readonly syncfieldPickerInput = ko.observable('')

    private stepDetails: IStepDetails[] = [
        {step: Step.ExportTemplate, name: 'synchronization template', fraction: true},
        {step: Step.Name, name: 'general', fraction: true},
        {step: Step.Optional, name: 'optional field|p', fraction: true},
        {step: Step.Members, name: 'member|p', fraction: true},
        {step: Step.Summary, name: 'summary', fraction: true},
        {step: Step.Processing, name: CREATING_MESSAGE, prev: Step.Summary},
        {step: Step.Success, name: 'completed', prev: Step.Summary},
        {step: Step.Error, name: 'error', prev: Step.Summary},
    ]

    protected data!: IObjectData;
    private selectedSynchronizationTemplate: KnockoutComputed<IExportTemplate | null>;

    constructor(params: IRouteDefinitionParams) {
        super(params);
        const cancelBtn = getCancelButton(undefined, {
            validator: () => !this.isStep(Step.Success, Step.Error),
        })
        if (cancelBtn) {
            this.pageButtons.add(cancelBtn)
        }
        const exitBtn = getExitButton(undefined, {
            validator: () => this.isStep(Step.Success, Step.Error),
        })
        if (exitBtn) {
            this.pageButtons.add(exitBtn)
        }
        this.pageButtons.add(getPreviousButton({
            onClick: this.prevStepClick.bind(this),
            enabled: () => !this.isStep(0),
            validator: () => this.isStep(Step.Name, Step.Optional, Step.Members, Step.ExportTemplate, Step.Summary, Step.Error),
        }))
        this.pageButtons.add(getNextButton({
            onClick: this.nextStepClick.bind(this),
            enabled: this.isValid.bind(this),
            validator: () => this.isStep(Step.Name, Step.Optional, Step.Members, Step.ExportTemplate),
        }))
        this.pageButtons.add(getSubmitButton({
            onClick: this.nextStepClick.bind(this),
            enabled: this.isValid.bind(this),
            validator: () => this.isStep(Step.Summary),
        }))
        this.pageButtons.add({
            cssClass: 'primary action',
            href: () => this.groupLink,
            icon: 'fa-manual-group',
            label: 'see details',
            order: ButtonOrder.DETAILS,
            validator: () => this.isStep(Step.Success),
        })
        this.pageButtons.add(getDiscardButton({
            onClick: () => {
                this.step(null)
                this.resetSyncfields()
                this.step(0)
            },
            icon: 'fa-redo',
            label: 'start over',
            order: ButtonOrder.START_OVER,
            validator: () => this.isStep(Step.Success, Step.Error),
        }))

        this.kosub.subscribe(this.step, (step: Step | null) => {
            if (step !== null) {
                this.onStepChange(step)
            }
        })
        this.kosub.subscribe(lm.selectedLanguage, () => {
            const step = this.step()
            this.step(null)
            this.messageData({message: PLEASE_WAIT_MESSAGE})
            nsSyncFields.getSyncFields(ObjectTypeId.Group)
                .request
                .then((data: ISyncField<TSyncfield_Group>[]) => {
                    this.syncfields_all = data;
                    this.messageData(null)
                    this.step(step)
                })
        })
        this.kosub.subscribe(this.isNameAndSourceIdSame, this.updateSourceId.bind(this));

        this.selectedSynchronizationTemplate = ko.pureComputed((): IExportTemplate | null => {
            const mutable_parent = this.getMutable('Parent');
            let id: string | undefined
            if (mutable_parent?.id?.()) {
                id = mutable_parent.id()!.toString()
            } else if (mutable_parent?.value?.()) {
                id = mutable_parent.value()!
            }
            if (!id) {
                return null
            }
            return this.exportTemplates.find(et => et.id.toString() == id) ?? null
        })
    }

    protected init(): Promise<void> {
        this.step(null);
        this.messageData({message: REQUEST_MESSAGE});

        return Promise.resolve()
            .then(() => {
                return nsSyncFields.getSyncFields(ObjectTypeId.Group)
                    .request
                    .then((data: ISyncField<TSyncfield_Group>[]) => {
                        this.syncfields_all = data;
                        Syncfield_mandatory.forEach(k => this.produceMutable(k))
                        this.kosub.subscribe(this.produceMutable('Name').value, this.updateSourceId.bind(this));
                    })
            })
            .then(() => {
                return this.req_getconditions
                    .request()
                    .then((data: ICondition[]) => {
                        this.resp_getconditions = data
                    })
            })
            .then(() => {
                return this.req_searchexporttemplate
                    .request(getSearchParamsFn('searchexporttemplate')({
                        resultsprpage: MAX_RESULTS_PER_PAGE
                    }))
                    .then((data: IExportTemplate[]) => {
                        this.exportTemplates = data
                    })
            })
    }

    protected onStepChange(step: Step) {
        this.messageData(null)
        switch (step) {
            case Step.Name: {
                const sf_sourceid = this.produceMutable('SourceId').value();
                const sf_name = this.produceMutable('Name').value();
                this.isNameAndSourceIdSame(sf_sourceid == sf_name)
                break
            }
            case Step.ExportTemplate: {
                const parentMutable = this.getMutable('Parent');
                const value = parentMutable?.value() ?? '';
                let isExportTemplate = false
                this.exportTemplates.find(o => {
                    const parentArgument = this.getParentArgumentFromExportTemplate(o);
                    isExportTemplate = this.isValueInParentArgument(value, parentArgument);
                    return isExportTemplate
                })
                this.parentType(isExportTemplate ? this.ParentType.Synctemplate : this.ParentType.Parent)
                break
            }
            case Step.Processing: {
                this.onSubmit();
                break
            }
        }

    }

    private getDisplayValue(key: TSyncfield_Group): string {
        let ret = this.getSubmitValue(key)
        if (ret === '') {
            return lm.t(EMPTY_STRING) ?? ''
        }
        if (ret === null) {
            return 'null'
        }
        if (ret === undefined) {
            return 'undefined'
        }
        return ret
    }

    private getSyncfield(key: TSyncfield_Group): ISyncField | undefined {
        return this.produceMutable(key).syncfield;
    }

    protected produceMutable(key: TSyncfield_Group): ISyncFieldMutable {
        let mutable = this.getMutable(key);
        if (!mutable) {
            mutable = this._generateMutable(key)
            this.mutables.push(mutable)
        }
        return mutable
    }

    protected getMutable(key: TSyncfield_Group): ISyncFieldMutable | undefined {
        return this.mutables().find(sf => sf.name == key)
    }

    // Note: use produce mutable to streamline getting and setting mutable
    protected _generateMutable(key: TSyncfield_Group): ISyncFieldMutable {
        const isMandatory = Syncfield_mandatory.includes(key)
        return {
            id: ko.observable<number | null>(null),
            mandatory: isMandatory,
            name: key,
            syncfield: this.syncfields_all.find(o => o.name == key),
            value: ko.observable<string | null>(null),
        }
    }

    private isStepValid(): boolean {
        switch (this.step()) {
            case Step.Name: {
                const a = !!this.getSubmitValue('Name');
                this.et.update(!a, 'Name', 'invalid value');
                const b = !!this.getSubmitValue('SourceId');
                this.et.update(!b, 'SourceId', 'invalid value');
                return a && b;
            }
            default:
                return true;
        }
    }

    protected isStep(...steps: Step[]): boolean {
        const step = this.step();
        if (step === null) {
            return false
        }
        return isPartOf<Step>(step, ...steps)
    }

    private getDepartmentText(obj: IObjectData): string {
        return getSyncFieldValue(getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName ?? [], obj.syncfields ?? []);
    }

    protected getSubmitValue(key: TSyncfield_Group): string | null {
        const sfm = this.getMutable(key)
        if (!sfm) {
            return null
        }
        const value = sfm.value() ?? null
        switch (key) {
            case 'ExpiryDate':
                if (isNullOrUndefinedOrEmptyString(value)) {
                    return ''
                }
                return convertToServerString(convertFromServerString(value!))
            default:
                return value
        }
    }

    protected nextStepClick() {
        const currentStep = this.step();
        if (currentStep === null) {
            return;
        }
        const step: Step = currentStep + 1;
        this.step(step);
    }

    protected prevStepClick() {
        const currentStep = this.step();
        if (currentStep === null) {
            return;
        }
        let step: Step
        const details = this.getCurrentStepDetails();
        if (details && 'prev' in details) {
            step = details.prev!
        } else {
            step = currentStep - 1
        }
        this.step(step);
    }

    protected updateSourceId() {
        if (this.isNameAndSourceIdSame()) {
            const name = this.produceMutable('Name').value();
            this.produceMutable('SourceId').value(name)
        }
    }

    private syncfields_unused(): ISyncField<TSyncfield_Group>[] {
        return this.syncfields_all
            .filter(sf => [...Syncfield_mandatory, ...Syncfield_optional].includes(sf.name))
            .filter(sf => !this.getMutable(sf.name))
    }

    private getComboboxParams_syncfield(): IComboboxV2Options<ISyncField<TSyncfield_Group>> {
        return {
            inputAttrs: {placeholder: lm.t('add subject|s:((synchronization field|ac))') ?? ''},
            data: ko.pureComputed(() => this.syncfields_unused()),
            input: this.syncfieldPickerInput,
            selectionMode: 'pick',
            option: {
                label: o => getSourceTranslation(o) ?? '',
                tooltip: o => o ? (lm.t(nsTooltip.getTitle_syncfield(o) ?? '') ?? '') : '',
            },
            reloadWhen: global.sourceTranslation,
            onSelect: (o) => {
                if (o) {
                    this.produceMutable(o.name)
                }
            },
            enable: () => !!this.syncfields_unused().length,
        }
    }

    private onRemoveClick(obj: ISyncFieldMutable) {
        this.mutables.remove(obj)
    }

    private getSourceTranslation(sf: ISyncField, translate: boolean = global.sourceTranslation()): string {
        return getSourceTranslation(sf, translate)
    }

    private getSyncfieldComponent(keyOrMutable: TSyncfield_Group | ISyncFieldMutable<TSyncfield_Group>): IKnockoutComponent | null {
        if (!keyOrMutable) {
            return null
        }
        const sfm: ISyncFieldMutable<TSyncfield_Group> = (typeof keyOrMutable == 'string')
            ? this.produceMutable(keyOrMutable)
            : keyOrMutable

        switch (sfm.name) {
            case 'ExpiryDate': {
                const options: IPickerOptions<string> = {
                    onSelect: s => sfm.value(s ? convertToServerString(moment(s)) : null),
                    value: sfm.value,
                }
                return {
                    name: 'component/picker/date',
                    params: options,
                }
            }
            case 'Manager': {
                const tuples = nsSyncFields.getSourceToTargetTuples({
                    srcObjectTypeId: ObjectTypeId.Group,
                    trgObjectTypeId: ObjectTypeId.User,
                    key: 'Manager'
                })
                const key = tuples.find(t => t[4] == 'Er lik')?.[3];
                if (!key) {
                    return null
                }
                const managerValue = this.getMutable('Manager')?.value() ?? '';
                const options: IPickerOptions<IObjectData> = {
                    onSelect: o => {
                        const value = getRawSyncFieldValue(key, o.syncfields) ?? null
                        sfm.value(value)
                    },
                    value: sfm.value,
                    additionalSyncfieldKeys: [key],
                    searchParams: getSearchObjectParams({
                        jruleset: JSON.stringify(nsRuleset.generateRuleset({
                            rules: [
                                nsRuleset.generateRule({
                                    attribute: key,
                                    argumentoperator: 'Er lik',
                                    argument: managerValue,
                                }),
                            ]
                        }))
                    })
                }
                return {
                    name: 'component/picker/user',
                    params: options,
                }
            }
            case 'MemberRuleSetId': {
                const options: IComboboxOptions_Ruleset = {
                    comboboxOptions: {
                        initialMatch: o => o.id.toString() == sfm.value(),
                        onSelect: (o) => {
                            sfm.value(o?.id.toString() ?? null)
                        },
                    },
                    showIcon: true,
                    objectTypeIds: ObjectTypeId.User,
                }
                return {
                    name: 'component/combobox/ruleset',
                    params: options,
                }
            }
            case 'OrgUnitnr':
            case 'ParentDepartmentNumber': {
                const trgKey = nsSyncFields.getSourceToTargetTuples({
                    srcObjectTypeId: ObjectTypeId.Group,
                    trgObjectTypeId: ObjectTypeId.Department,
                    key: sfm.name,
                })?.[0]?.[3]
                if (!trgKey) {
                    return null
                }
                const keys: TSyncfield_Department[] = ['Id', 'Name', 'Department', 'SourceId', 'DepartmentNumber', 'OrgUnitnr'];
                const options: IComboboxOptions_Department = {
                    comboboxOptions: {
                        initialMatch: o => getRawSyncFieldValue(trgKey, o.syncfields) == sfm.value(),
                        onSelect: (o) => {
                            if (o) {
                                sfm.value(getRawSyncFieldValue(trgKey, o.syncfields) ?? null)
                            } else {
                                sfm.value(null)
                            }
                        },
                    },
                    keys: keys,
                    showIcon: true,
                }
                return {
                    name: 'component/combobox/department',
                    params: options,
                }
            }
            case 'Parent': {
                const params: IComboboxV2Options<IExportTemplate> = {
                    prepareData: {
                        filter: o => {
                            const a = o.objecttype.id == ObjectTypeId.Group
                            const b = !!o.ruleset?.rules.find(r => r.attribute == 'Parent')
                            const c = o.active
                            return a && b && c
                        },
                    },
                    inputAttrs: {placeholder: lm.t('synchronization template') || ''},
                    initialMatch: o => {
                        if (!o) {
                            return false
                        }
                        const parentArgument = this.getParentArgumentFromExportTemplate(o)
                        const value = sfm.value() ?? ''
                        if (Array.isArray(parentArgument)) {
                            return parentArgument.includes(value)
                        }
                        return parentArgument == value
                    },
                    option: {
                        label: o => o?.name ?? '',
                        tooltip: o => {
                            if (!o) {
                                return ''
                            }
                            return lm.t(nsTooltip.getTitle_exportTemplate(o) ?? '') || ''
                        },
                    },
                    onInitialMatchResolved: o => {
                        if (o) {
                            sfm.id(o.id)
                        }
                        return false
                    },
                    onSelect: o => {
                        if (!o) {
                            return
                        }
                        const parentArgument = this.getParentArgumentFromExportTemplate(o)
                        const currentValue = sfm.value() ?? ''
                        if (Array.isArray(parentArgument) && parentArgument.includes(currentValue)) {
                            // already set - return
                            return
                        }
                        const val = Array.isArray(parentArgument)
                            ? parentArgument[0]
                            : parentArgument
                        sfm.id(o.id ?? null)
                        sfm.value(val ?? null)
                    },
                    data: this.exportTemplates,
                }
                return {
                    name: 'component/combobox/v2',
                    params: params,
                }
            }
            default:
                return null
        }
    }

    private getSyncfieldReadComponent(sfm: ISyncFieldMutable<TSyncfield_Group>): IKnockoutComponent | null {
        if (!sfm.value()) {
            return null
        }
        const params = {id: sfm.value(), linkable: true, showIcon: true, target: '_blank'};
        switch (sfm.name) {
            case 'Manager': {
                const tuples = nsSyncFields.getSourceToTargetTuples({
                    srcObjectTypeId: ObjectTypeId.Group,
                    trgObjectTypeId: ObjectTypeId.User,
                    key: 'Manager'
                })
                const tuple = tuples.find(t => t[4] == 'Er lik');
                if (!tuple) {
                    return null
                }
                const _params = _.defaultsDeep({
                    argument: this.getMutable('Manager')?.value() ?? '',
                    argumentoperator: tuple[4],
                    attribute: tuple[3],
                    objectTypeId: ObjectTypeId.User,
                }, params)
                return {
                    name: 'component/variable/object-by-any',
                    params: _params,
                }
            }
            case 'MemberRuleSetId': {
                return {
                    name: 'component/variable/ruleset',
                    params: params,
                }

            }
            case 'OrgUnitnr':
            case 'ParentDepartmentNumber': {
                return {
                    name: 'component/variable/object-by-any',
                    params: _.defaults({
                        argument: sfm.value(),
                        attribute: nsSyncFields.getSourceToTargetTuples({
                            srcObjectTypeId: ObjectTypeId.Group,
                            trgObjectTypeId: ObjectTypeId.Department,
                            key: sfm.name,
                        })?.[0]?.[3],
                        objectTypeId: ObjectTypeId.Department,
                        showNoData: false,
                    }, params),
                }

            }
            case 'Parent': {
                if (Number(sfm.value())) {
                    return {
                        name: 'component/variable/exporttemplate',
                        params: params,
                    }
                }
                return null
            }
            default:
                return null
        }
    }

    private getSyncfieldReadDerived(sfm: ISyncFieldMutable<TSyncfield_Group>): string | null {
        if (!sfm.value()) {
            return null
        }
        switch (sfm.name) {
            case 'ExpiryDate': {
                return convertToServerString(moment(sfm.value()!, INPUT_DATE_FORMAT), true);
            }
            default:
                return null
        }
    }

    private getRowBindings(key: TSyncfield_Group) {
        const syncfield = this.syncfields_all.find(o => o.name == key)
        const mutable = this.getMutable(key);
        const syncfieldtype = 'syncfield-' + (this.mutables().find(sfm => sfm == mutable)?.mandatory ? 'mandatory' : 'optional')
        const componentName = this.getSyncfieldComponent(mutable ?? key)?.name;
        return {
            attr: {
                'data-fieldtype': syncfield?.fieldtype,
                'data-syncfieldname': key,
                'data-component': componentName,
            },
            cls: 'syncfield ' + syncfieldtype,
        }
    }

    private getCurrentStepDetails(): IStepDetails | undefined {
        const step = this.step();
        if (step === null) {
            return undefined
        }
        return this.stepDetails[step]
    }

    private getCurrentStepFraction(): string | null {
        if (!this.getCurrentStepDetails()?.fraction) {
            return null
        }
        const current = this.step()
        if (current === null) {
            return null
        }
        const total = this.stepDetails.filter(o => o.fraction).length
        return `${current + 1}/${total}`
    }

    protected isValid = ko.pureComputed((): boolean => {
        return this.isStepValid()
    });

    protected abstract getSubmitData(): Partial<IObjectData>

    protected abstract getSubmitSyncfields(): ISyncField[]

    protected abstract onSubmit(): Promise<any>

    protected abstract resetSyncfields(): void

    protected onSubmitSuccess(data: [IObjectData]) {
        this.data = data[0]
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache('searchobject');
        global.cacheManager.removeCache('getobject');
        this.step(Step.Success);
    }

    protected onRequestError(reason: string): void {
        this.step(Step.Error);
        this.messageData(createErrorMessage(String(reason)));
    }

    private getStepInfo = ko.pureComputed((): string | null => {
        if (this.messageData()) {
            return null
        }
        const step = this.step();
        if (step === null) {
            return null
        }
        const id = 'group.wizard.info.' + (step + 1);
        if (lm.hasTranslation(id)) {
            return lm.t(id, {br: true})
        }
        return null
    })

    private getParentArgumentFromExportTemplate(et: IExportTemplate): string | string[] | null {
        if (!et?.ruleset?.rules) {
            return null
        }
        const rule = et.ruleset.rules.find(r => r.attribute == 'Parent');
        if (!rule) {
            return null
        }
        const operator = this.resp_getconditions.find(o => o.name == rule.argumentoperator);
        if (!operator) {
            return null
        }
        if (operator.name.toLowerCase().includes('ikke')) {
            return et.id.toString()
        }

        const ret = rule.argument.toString()
        return operator.type == 'array'
            ? ret.split(',')
            : ret
    }

    private isValueInParentArgument(val: string, parentArgument: string | string[] | null): boolean {
        if (parentArgument === null) {
            return false
        }
        return Array.isArray(parentArgument)
            ? parentArgument.includes(val)
            : parentArgument == val
    }

    override dispose() {
        this.kosub.unsuscribeAll()
        this.req_submit.abort()
        this.req_searchexporttemplate.abort()
        this.req_getconditions.abort()
    }
}